import { Box, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

import { CoAppActionButton, CoAppCancelTextButton } from "../../global/styled/global.styled";
import NodeDrawer from "../drawers/NodeDrawer";
import { appScrollbarSx } from "./styled/CanvasFormTextField.styled";

export interface BaseFormProps {
	open: boolean;
	onClose: () => void;
	onSave: () => void;
	title: string;
	icon: ReactNode;
	iconColor?: string;
	/**
	 * Optional gradient string used for the form's header icon tile and
	 * title text. When provided, the icon renders as a filled gradient tile
	 * with a white glyph (matching the AddNodeMenu items) and the title is
	 * painted with the same gradient via background-clip. Falls back to the
	 * legacy `iconColor` style when omitted.
	 */
	gradient?: string;
	children: ReactNode;
	isSaving?: boolean;
	isSaveDisabled?: boolean;
	saveButtonText?: string;
	width?: number | string;
	/**
	 * Aggregate error messages to announce when the user attempts to save
	 * an invalid form. Rendered inside a role="alert" aria-live region so
	 * screen readers hear the issues without the user hunting for the
	 * inline TextField helper text. WCAG 2.1 AA 3.3.1.
	 */
	errors?: string[];
}

// Inline form panel that sits beside the canvas (NodeDrawer is no longer a
// modal). Header → scrollable body → action footer, all inside the same
// non-shadowed container.

// Header, body, and footer now sit flush to the panel with one consistent
// horizontal padding (20px), so their content and the footer divider all reach
// the panel's edges instead of floating in a centered max-width column.

const BaseForm = (props: BaseFormProps) => {
	const {
		open,
		onClose,
		onSave,
		title,
		icon,
		iconColor = "#02D5AB",
		gradient,
		children,
		isSaving = false,
		isSaveDisabled = false,
		saveButtonText = "Save",
		width = 460,
		errors = []
	} = props;

	const hasGradient = Boolean(gradient);

	// Pull the two stop colors out of the `linear-gradient(135deg, A 0%, B 100%)`
	// string so we can build a real SVG <linearGradient> and paint the MUI icon
	// glyph itself with the gradient (background-clip:text only works on text,
	// not SVG paths — so the glyph needs fill: url(#id)).
	const gradientStops = hasGradient ? (gradient!.match(/#[0-9a-fA-F]{3,8}/g) ?? []) : [];
	const gradId =
		gradientStops.length >= 2
			? `bfGrad-${gradientStops[0].slice(1)}-${gradientStops[1].slice(1)}`
			: undefined;
	const iconFill = gradId ? `url(#${gradId})` : iconColor;

	return (
		<NodeDrawer open={open} onClose={onClose} width={width}>
			{/* SVG gradient def used to fill the header icon glyph. Zero-size,
			    purely a <defs> carrier referenced by fill: url(#…). */}
			{gradId && gradientStops.length >= 2 && (
				<svg width={0} height={0} style={{ position: "absolute" }} aria-hidden focusable="false">
					<defs>
						<linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor={gradientStops[0]} />
							<stop offset="100%" stopColor={gradientStops[1]} />
						</linearGradient>
					</defs>
				</svg>
			)}
			{/* Header row. The content is constrained to FORM_CONTENT_MAX_WIDTH
			    and centered so the header, body, and footer all share one tidy
			    column. The header divider was intentionally removed earlier. */}
			<Box sx={{ flexShrink: 0 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.25,
						padding: "14px 20px",
						width: "100%",
						boxSizing: "border-box"
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 32,
							height: 32,
							borderRadius: 1.5,
							// Inverted from the old AddNodeMenu style: the tile is now
							// a SUBTLE low-opacity tint of the color, and the glyph
							// itself carries the gradient (via fill: url(#…)).
							background: `${iconColor}1F`,
							color: iconColor,
							boxShadow: "none",
							"& > *": { fontSize: 18 },
							"& svg": { fill: iconFill }
						}}
					>
						{icon}
					</Box>
					<Typography
						sx={{
							fontSize: 11,
							fontWeight: 700,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
							// Title text picks up the same gradient via
							// background-clip when available; otherwise falls
							// back to the solid icon color.
							...(hasGradient
								? {
									backgroundImage: gradient,
									WebkitBackgroundClip: "text",
									backgroundClip: "text",
									color: "transparent"
								}
								: { color: iconColor })
						}}
					>
						{title}
					</Typography>
				</Box>
			</Box>

			{/* Live region for screen-reader error announcements. Visible
			    summary above the body whenever errors[] is non-empty so
			    sighted users see the same callout. WCAG 3.3.1. */}
			{errors.length > 0 && (
				<Box
					role="alert"
					aria-live="assertive"
					sx={{
						width: "100%",
						boxSizing: "border-box",
						px: "20px",
						mt: 1.5,
						borderRadius: 1,
						"& > div": {
							px: 1.5,
							py: 1,
							borderRadius: 1,
							background: "rgba(232, 80, 91, 0.08)",
							border: "1px solid rgba(232, 80, 91, 0.30)",
							fontSize: 12,
							color: "#B81E15",
						}
					}}
				>
					<div>
						{errors.length === 1
							? errors[0]
							: `${errors.length} errors: ${errors.join("; ")}`}
					</div>
				</Box>
			)}

			{/* Scrollable form body — flush to the panel with consistent padding. */}
			<Box sx={{ flex: 1, overflowY: "auto", ...appScrollbarSx }}>
				<Box sx={{ width: "100%", boxSizing: "border-box", padding: "16px 20px" }}>
					{children}
				</Box>
			</Box>

			{/* Action footer — full-bleed top divider, buttons aligned to the
			    same centered content column as the body. */}
			<Box
				sx={{
					flexShrink: 0,
					background: "#FFFFFF",
					borderTop: "1px solid rgba(13, 31, 48, 0.06)"
				}}
			>
				<Stack
					direction="row"
					spacing={1.25}
					sx={{
						width: "100%",
						boxSizing: "border-box",
						padding: "12px 20px",
						// Both buttons share the row equally (50/50) and span the
						// full footer width.
						"& > *": { flex: 1 }
					}}
				>
					<CoAppCancelTextButton onClick={onClose} disabled={isSaving}>
						Cancel
					</CoAppCancelTextButton>
					<CoAppActionButton
						onClick={onSave}
						disabled={isSaveDisabled}
					>
						{isSaving ? "Saving…" : saveButtonText}
					</CoAppActionButton>
				</Stack>
			</Box>
		</NodeDrawer>
	);
};

export default BaseForm;
